import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";

const FORM_URL = "https://readdy.ai/api/form/dafuq1ptto0ceqqnhi40";

const contactChannels = [
  { icon: "ri-mail-fill", title: "邮箱", desc: "feedback@zgchigua.cn", tone: "bg-primary-100 text-primary-700" },
  { icon: "ri-time-fill", title: "在线时间", desc: "工作日 9:00 - 21:00", tone: "bg-accent-100 text-accent-700" },
  { icon: "ri-chat-1-fill", title: "内容反馈", desc: "纠错 / 侵权举报 24h 响应", tone: "bg-secondary-100 text-secondary-900" },
];

const topicOptions = ["内容纠错", "侵权举报", "商务合作", "媒体转载", "其他建议"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    // honeypot guard: silently succeed without sending
    const honeypot = ((fd.get("company_alt") as string) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("感谢你的反馈！我们会尽快查看并回复你。");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      const v = value as string;
      if (!v.trim()) return;
      if (key === "company_alt") return;
      payload.append(key, v.trim());
    });

    setStatus("submitting");
    setFormError("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setSuccessMsg("感谢你的反馈！我们会尽快查看并回复你。");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg ? String(serverMsg) : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  const contactLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 在线吃瓜",
    url: "https://www.zgchigua.cn/contact",
    inLanguage: "zh-CN",
  };

  return (
    <Layout>
      <Seo title="联系我们_意见反馈与内容合作_在线吃瓜" description="联系在线吃瓜：内容纠错、侵权举报、商务合作与媒体转载请通过此页面提交，亦可发送邮件至 feedback@zgchigua.cn，我们会在工作日及时回复。" keywords="联系我们,在线吃瓜反馈,侵权举报,内容纠错,商务合作" path="/contact" jsonLd={[contactLd]} />

      <section className="container-x py-8 md:py-12">
        <nav className="flex items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>联系我们</span>
        </nav>

        <div className="mt-5 max-w-2xl">
          <h1 className="font-heading text-2xl md:text-4xl">和我们聊聊</h1>
          <p className="mt-3 text-foreground-600">
            无论是发现内容有误、希望转载合作，还是对我们的平台有想法，都欢迎留言。你的每一条反馈，都在帮助我们做得更真、更准、更有温度。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4 space-y-4">
            {contactChannels.map((c) => (
              <div key={c.title} className="card-surface flex items-center gap-4 p-4">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${c.tone}`}><i className={`${c.icon} text-xl`}></i></span>
                <div>
                  <h2 className="font-bold">{c.title}</h2>
                  <p className="text-sm text-foreground-600">{c.desc}</p>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-gradient-to-br from-primary-600 to-secondary-500 p-5 text-background-50">
              <h2 className="font-heading text-lg">先看看常见问题？</h2>
              <p className="mt-1 text-sm text-background-100/90">关于榜单更新、热度算法与内容来源，我们整理了一份 FAQ，也许能帮你快速找到答案。</p>
              <Link to="/" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-accent-200 hover:underline">回到首页查看 <i className="ri-arrow-right-line"></i></Link>
            </div>
          </div>

          <div className="lg:col-span-8">
            <form id="contact-form" data-readdy-form onSubmit={onSubmit} className="card-surface p-5 md:p-7">
              <h2 className="font-heading text-xl">填写反馈表单</h2>

              <div className="frm-guard-extra" aria-hidden="true">
                <label htmlFor="company_alt">Company</label>
                <input type="text" id="company_alt" name="company_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">怎么称呼你</label>
                  <input id="name" name="name" type="text" required placeholder="你的昵称或姓名" className="w-full rounded-lg bg-background-50 border border-background-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱</label>
                  <input id="email" name="email" type="email" required placeholder="you@example.com" className="w-full rounded-lg bg-background-50 border border-background-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">反馈主题</label>
                <select id="subject" name="subject" required className="w-full cursor-pointer rounded-lg bg-background-50 border border-background-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100">
                  {topicOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">想说的话</label>
                <textarea id="message" name="message" required maxLength={500} rows={6} placeholder="请尽量描述具体的问题、链接或你的想法（500 字以内）" className="w-full resize-none rounded-lg bg-background-50 border border-background-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"></textarea>
                <div className="mt-1 text-right text-xs text-foreground-400">最多 500 字</div>
              </div>

              {status === "success" && (
                <div className="mt-4 flex items-start gap-3 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-900">
                  <i className="ri-checkbox-circle-fill mt-0.5"></i>
                  {successMsg}
                </div>
              )}
              {status === "error" && (
                <div className="mt-4 flex items-start gap-3 rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-error-warning-fill mt-0.5"></i>
                  {formError || "提交失败，请稍后再试。"}
                </div>
              )}

              <button type="submit" disabled={status === "submitting"} className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 提交中...
                  </>
                ) : (
                  <>
                    提交反馈 <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}